import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  Search,
  Download,
  User,
  Gamepad2,
  ArrowDownLeft,
  ArrowUpRight,
  LogIn,
  LogOut,
  Shield,
  Smartphone,
  MapPin,
  RefreshCcw,
  TrendingUp,
  Eye,
  MoreHorizontal,
  SlidersHorizontal,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
  FilterPanel,
} from '@/components/ui/Table';
import { Tabs, DropdownMenu } from '@/components/ui/Modal';
import {
  formatDateTime,
  formatRelativeTime,
  formatCurrency,
  formatPhone,
  getInitials,
  cn,
} from '@/lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { activityApi } from '@/lib/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface ActivityItem {
  _id: string;
  userId: string;
  userName: string;
  userPhone: string;
  userAvatar: string | null;
  activityType: string;
  description: string;
  metadata: Record<string, any>;
  ipAddress: string;
  device: string;
  platform: string;
  location: string;
  createdAt: string;
}

const getActivityIcon = (type: string) => {
  const icons: Record<string, any> = {
    game_played: Gamepad2,
    deposit: ArrowDownLeft,
    withdrawal_request: ArrowUpRight,
    login: LogIn,
    logout: LogOut,
    kyc_submitted: Shield,
    referral_bonus: User,
    registration: User,
  };
  return icons[type] || Activity;
};

const getActivityColor = (type: string) => {
  const colors: Record<string, string> = {
    game_played: 'text-violet-400 bg-violet-500/10',
    deposit: 'text-emerald-400 bg-emerald-500/10',
    withdrawal_request: 'text-amber-400 bg-amber-500/10',
    login: 'text-blue-400 bg-blue-500/10',
    logout: 'text-slate-400 bg-slate-500/10',
    kyc_submitted: 'text-pink-400 bg-pink-500/10',
    referral_bonus: 'text-cyan-400 bg-cyan-500/10',
    registration: 'text-emerald-400 bg-emerald-500/10',
  };
  return colors[type] || 'text-slate-400 bg-slate-500/10';
};

export default function UserActivity() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    activityType: '',
    platform: '',
    dateFrom: '',
    dateTo: '',
  });

  const itemsPerPage = 15;

  // Fetch activities from API
  const { data: activitiesData, isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['adminActivities', currentPage, searchQuery, filters],
    queryFn: async () => {
      try {
        const params: any = {
          page: currentPage,
          limit: 100,
        };
        if (searchQuery) params.search = searchQuery;
        if (filters.activityType) params.activityType = filters.activityType;
        if (filters.platform) params.platform = filters.platform;
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;

        const response = await activityApi.getActivities(params);
        return response.data.data;
      } catch (error: any) {
        console.error('Failed to fetch activities:', error);
        return { activities: [], pagination: { total: 0 } };
      }
    },
    staleTime: 30000,
  });

  // Fetch activity stats from API
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['adminActivityStats'],
    queryFn: async () => {
      try {
        const response = await activityApi.getStats();
        return response.data.data;
      } catch (error: any) {
        console.error('Failed to fetch activity stats:', error);
        return {
          activityByType: [],
          activityTimeline: [],
          stats: { games: 0, deposits: 0, withdrawals: 0, logins: 0, kyc: 0, referrals: 0 },
        };
      }
    },
    staleTime: 60000,
  });

  const activities: ActivityItem[] = activitiesData?.activities || [];
  const activityByTypeData = statsData?.activityByType || [];
  const activityChartData = statsData?.activityTimeline || [];

  const loading = activitiesLoading || statsLoading;

  // Filter activities by tab
  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    // Tab filter
    if (activeTab !== 'all') {
      if (activeTab === 'games') {
        filtered = filtered.filter((a) => a.activityType === 'game_played');
      } else if (activeTab === 'financial') {
        filtered = filtered.filter((a) =>
          ['deposit', 'withdrawal_request', 'referral_bonus'].includes(a.activityType)
        );
      } else if (activeTab === 'auth') {
        filtered = filtered.filter((a) =>
          ['login', 'logout', 'registration'].includes(a.activityType)
        );
      }
    }

    return filtered;
  }, [activities, activeTab]);

  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  const handleRefresh = async () => {
    await Promise.all([refetchActivities(), refetchStats()]);
    toast.success('Data refreshed');
  };
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Activity className="w-7 h-7 text-violet-400" />
            User Activity
          </h1>
          <p className="text-slate-400 mt-1">Monitor real-time user activities across the platform</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCcw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        {activityByTypeData.map((item: { type: string; count: number; color: string }) => (
          <Card key={item.type} variant="glass" className="p-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <TrendingUp className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{item.count.toLocaleString()}</p>
                <p className="text-xs text-slate-400">{item.type}</p>
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      {/* Charts */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Timeline */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle>Activity Timeline (Today)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={activityChartData}>
                  <defs>
                    <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="hour" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: '12px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="activities"
                    stroke="#8B5CF6"
                    fill="url(#activityGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Activity by Type */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle>Activity by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityByTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" stroke="#64748B" fontSize={12} />
                  <YAxis type="category" dataKey="type" stroke="#64748B" fontSize={12} width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: '12px',
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {activityByTypeData.map((entry: { type: string; count: number; color: string }, index: number) => (
                      <rect key={`bar-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <Tabs
          tabs={[
            { id: 'all', label: 'All Activities', badge: activities.length },
            { id: 'games', label: 'Games' },
            { id: 'financial', label: 'Financial' },
            { id: 'auth', label: 'Auth & Sessions' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="pills"
        />
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by user name, phone, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                />
              </div>
              <Button
                variant={showFilters ? 'default' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>

            {showFilters && (
              <FilterPanel isOpen={showFilters} onClose={() => setShowFilters(false)}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <select
                    value={filters.activityType}
                    onChange={(e) => setFilters((prev) => ({ ...prev, activityType: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="">Activity Type</option>
                    <option value="game_played">Game Played</option>
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal_request">Withdrawal</option>
                    <option value="login">Login</option>
                    <option value="registration">Registration</option>
                    <option value="kyc_submitted">KYC Submitted</option>
                    <option value="referral_bonus">Referral Bonus</option>
                  </select>
                  <select
                    value={filters.platform}
                    onChange={(e) => setFilters((prev) => ({ ...prev, platform: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="">Platform</option>
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                    <option value="web">Web</option>
                  </select>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    placeholder="From Date"
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                    placeholder="To Date"
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() =>
                    setFilters({
                      activityType: '',
                      platform: '',
                      dateFrom: '',
                      dateTo: '',
                    })
                  }
                >
                  Clear Filters
                </Button>
              </FilterPanel>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Activity Table */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="w-12">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedActivities.map((activity) => {
                  const Icon = getActivityIcon(activity.activityType);
                  const colorClass = getActivityColor(activity.activityType);

                  return (
                    <TableRow key={activity._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white font-medium text-sm">
                            {getInitials(activity.userName)}
                          </div>
                          <div>
                            <p className="font-medium text-white">{activity.userName}</p>
                            <p className="text-xs text-slate-500">{formatPhone(activity.userPhone)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', colorClass)}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-slate-300 capitalize">
                            {activity.activityType.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-300 max-w-xs">{activity.description}</p>
                        {activity.metadata.amount && (
                          <p className="text-xs text-emerald-400 mt-0.5">
                            {formatCurrency(activity.metadata.amount)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="text-sm text-slate-300">{activity.device}</p>
                            <p className="text-xs text-slate-500">{activity.platform}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="text-sm text-slate-300">{activity.location}</p>
                            <p className="text-xs text-slate-500">{activity.ipAddress}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-300">{formatRelativeTime(activity.createdAt)}</p>
                        <p className="text-xs text-slate-500">{formatDateTime(activity.createdAt)}</p>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu
                          trigger={
                            <button className="p-2 rounded-lg hover:bg-slate-800 transition-colors">
                              <MoreHorizontal className="w-4 h-4 text-slate-400" />
                            </button>
                          }
                          items={[
                            {
                              label: 'View User',
                              icon: User,
                              onClick: () => navigate(`/users/${activity.userId}`),
                            },
                            {
                              label: 'View Details',
                              icon: Eye,
                              onClick: () => toast.success('Opening activity details...'),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {filteredActivities.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredActivities.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
